import React, { Component } from 'react';

import {
  BundleJson,
  BundlesPageJson,
  BundleStatus,
  CourierJson,
  InstructionType,
} from '../../types/bundleTypes';

import Fetch from 'utils/fetch';

import './BundleMonitor.scss';
import { OrderClazz, Region } from '../../types/orderTypes';
import { settings } from '../../settings';
import { getBundleColor } from '../oldPlanner/utils/bundleDraft';
import { flatten } from '../../utils/array';
import BundleMonitorMap, { OrderMarker } from './BundleMonitorMap';
import { fetchCourierByIdList } from '../../fetch/fetchUsers';
import MonitorBundleInfo from './MonitorBundleInfo';
import {
  bundleOrderIds,
  findNextInstruction,
} from '../bundles/components/BundleUtils';
import { fetchOrders } from '../../fetch/orderFetch';
import { plainToClass } from 'class-transformer';

interface State {
  orders: OrderClazz[];
  ordersById: Map<string, OrderClazz>;
  bundles: BundleJson[];
  couriers: CourierJson[];
  selectedBundle?: string;
}

interface Props {
  region: Region;
}

export default class BundleMonitor extends Component<Props, State> {
  constructor(props) {
    super(props);

    this.state = {
      orders: [],
      ordersById: new Map(),
      bundles: [],
      couriers: [],
      selectedBundle: null,
    };
  }

  async componentDidMount() {
    this.refreshState();
    setInterval(() => {
      const bundles = this.state.bundles || [];
      this.fetchCouriers(bundles);
    }, 30_000);

    setInterval(() => {
      this.refreshState();
    }, 60_000);
  }

  async componentDidUpdate(prevProps: Readonly<Props>, prevState: State) {
    if (this.props.region && prevProps.region !== this.props.region) {
      this.refreshState();
    }
  }

  refreshState = async () => {
    const region = this.props.region;
    const showBundleStatuses = [
      BundleStatus.Published,
      BundleStatus.Proposed,
      BundleStatus.Accepted,
    ].join(',');

    const bundleResponse = await Fetch.get({
      url: `${settings.newApiUrl}/bundle?status=${showBundleStatuses}&region=${region}`,
    });
    if (!bundleResponse.ok) {
      console.warn('Could not fetch bundles', bundleResponse);
      return;
    }
    const bundleJson = plainToClass(
      BundlesPageJson,
      await bundleResponse.json()
    );
    const bundles = bundleJson.items;

    const orderIds: string[] = flatten(
      bundles.map(bundle => bundleOrderIds(bundle))
    );

    const orders = (await fetchOrders(orderIds)).map(order => {
      return new OrderClazz(order);
    });
    const ordersById = new Map<string, OrderClazz>();
    orders.forEach(order => ordersById.set(order.id, order));

    this.setState(
      {
        bundles: bundles,
        orders: orders,
        ordersById: ordersById,
      },
      () => {
        this.fetchCouriers(bundles);
      }
    );
  };

  private async fetchCouriers(bundles: BundleJson[]) {
    const courierIds = bundles
      .filter(bundle => !!bundle.assignedCourierId)
      .map(bundle => Number(bundle.assignedCourierId));
    const couriersTable = await fetchCourierByIdList(courierIds);
    if (!couriersTable) {
      return;
    }
    const couriers = Object.values(couriersTable);
    this.setState({ couriers: couriers });
  }

  private getPreviousDeliveryIndex(bundle: BundleJson): number {
    const nextDelivery = findNextInstruction(bundle, InstructionType.Delivery);
    if (!nextDelivery) {
      return -1;
    }

    let previousDelivery = -1;
    for (let i = 0; i < bundle.instructions.length; i++) {
      const instr = bundle.instructions[i];
      if (
        instr.orderId === nextDelivery.orderId &&
        instr.type === nextDelivery.type
      ) {
        break;
      }
      if (instr.type === InstructionType.Delivery) {
        previousDelivery = i;
      }
    }
    return previousDelivery;
  }

  private generateOrderMarkers(
    bundles: BundleJson[],
    orders: OrderClazz[]
  ): OrderMarker[] {
    const ordersMap: Map<string, OrderClazz> = orders.reduce((map, order) => {
      map.set(order.id, order);
      return map;
    }, new Map());

    const nextDeliveryOrderIds = bundles.reduce((orderIds, bundle) => {
      const orderId = findNextInstruction(
        bundle,
        InstructionType.Delivery
      )?.orderId;
      if (orderId) {
        orderIds.add(orderId);
      }
      return orderIds;
    }, new Set<string>());

    const bundledMarkers: OrderMarker[] = flatten(
      bundles.map(currentBundle => {
        const bundleColor = getBundleColor(currentBundle.id);

        const prevDeliveryIndex = this.getPreviousDeliveryIndex(currentBundle);

        const ordersInBundles = Array.from(
          new Set(
            currentBundle.instructions
              .filter(
                (instruction, idx) =>
                  !instruction.completed || idx >= prevDeliveryIndex
              )
              .map(instruction => instruction.orderId)
          )
        )
          .filter(orderId => ordersMap.has(orderId))
          .map(orderId => ordersMap.get(orderId));

        return ordersInBundles.map(order => {
          return {
            order: order,
            color: bundleColor,
            nextDelivery: nextDeliveryOrderIds.has(order.id),
          };
        });
      })
    );

    return bundledMarkers;
  }

  getBundleOrders(
    bundle: BundleJson,
    ordersById: Map<string, OrderClazz>
  ): OrderClazz[] {
    return bundleOrderIds(bundle)
      .map(id => ordersById.get(id))
      .filter(order => !!order);
  }

  render() {
    const { orders, ordersById, bundles, couriers, selectedBundle } =
      this.state;

    const courierIds = new Set<number>();
    const selectedBundles = bundles.filter(bundle => {
      const selected =
        !selectedBundle || selectedBundle === bundle.id || bundles.length === 1;
      if (selected) {
        courierIds.add(Number(bundle.assignedCourierId));
      }
      return selected;
    });
    const selectedCouriers = couriers.filter(courier =>
      courierIds.has(courier.id)
    );
    const orderMarkers = this.generateOrderMarkers(selectedBundles, orders);

    return (
      <div className="bundle-monitor">
        <div className="bundle-monitor__filter-menu">
          <table className="monitor-bundle-info-table">
            <thead>
              <tr>
                <th />
                <th>Bundle</th>
                <th>Courier</th>
                <th>Orders</th>
                <th>Last</th>
                <th>Next</th>
                <th>Show</th>
              </tr>
            </thead>
            <tbody>
              {bundles.map(bundle => {
                return (
                  <MonitorBundleInfo
                    key={bundle.id}
                    orders={this.getBundleOrders(bundle, ordersById)}
                    bundle={bundle}
                    selected={
                      !this.state.selectedBundle ||
                      bundle.id === this.state.selectedBundle
                    }
                    onShowClick={() => {
                      if (this.state.selectedBundle === bundle.id) {
                        this.setState({
                          selectedBundle: null,
                        });
                      } else {
                        this.setState({
                          selectedBundle: bundle.id,
                        });
                      }
                    }}
                  />
                );
              })}
            </tbody>
          </table>
        </div>
        <BundleMonitorMap
          bundles={selectedBundles}
          orders={orderMarkers}
          couriers={selectedCouriers}
          drawRoutes={selectedBundles.length === 1}
        />
      </div>
    );
  }
}
